import React, { forwardRef, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    ListItem,
    Menu,
    MenuItem,
    paletteColors,
    Typography,
} from '@evaneos/b2b-ui';
import MoreVertRoundedIcon from '@evaneos/b2b-ui/icons/MoreVertRounded';
import { Link } from 'react-router-dom';
import { format, isToday } from 'date-fns';
import { useIntl } from 'react-intl';

import WarningIcon from '@evaneos/b2b-ui/icons/WarningAmber';

import { DisplayDossierOverview } from '../../../resources/dossier/types';
import { RepeaterBadge } from '../../../design-system/RepeaterBadge/RepeaterBadge';
import { useMarkAsNoResponseRequiredMutation } from '../../../resources/discussion/mutations';
import { NoResponseRequiredReason } from '../../../resources/discussion/types';
import { Avatar } from '../../../resources/agent/components/Avatar';
import { useDisplayedAgentFromFilter } from '../../../resources/filters/hooks';
import { useTrackEventAmplitude } from '../../../shared/analytics';
import { TRAVELLING_STEP } from '../../../domain';

type MessageItemProps = {
    dossier: DisplayDossierOverview;
    isResponseOverdue: boolean;
};

export const MessageItem = forwardRef<HTMLAnchorElement, MessageItemProps>(
    // eslint-disable-next-line max-lines-per-function -- message item with context menu, overdue state and amplitude tracking
    ({ dossier, isResponseOverdue = false }, ref) => {
        const { formatMessage } = useIntl();
        const { trackEvent } = useTrackEventAmplitude();
        const markAsNoResponseRequiredMutation = useMarkAsNoResponseRequiredMutation(dossier.id);
        const { id, project, requesterIsRepeater, discussion, requestDate, agentId, step } =
            dossier;
        const assignedAgent = useDisplayedAgentFromFilter(agentId);

        const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

        const actionsButtonAccessibilityId = `${id}-message-item-actions-button`;

        const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
            event.preventDefault();
            event.stopPropagation();
            setMenuAnchorEl(event.currentTarget);
        };
        const handleCloseMenu = (event: React.MouseEvent<HTMLElement>) => {
            event.preventDefault();
            event.stopPropagation();
            setMenuAnchorEl(null);
        };

        const handleMarkAsNoResponseRequired = (
            event: React.MouseEvent<HTMLElement>,
            reason: NoResponseRequiredReason,
        ) => {
            trackEvent((ampli, defaultProperties) => {
                ampli.quickActionUsed({
                    ...defaultProperties,
                    action_type:
                        reason === 'external_answer_done'
                            ? 'answered_outside_RM'
                            : 'mark_no_response_needed',
                    message_id: discussion.lastTravellerUnansweredMessageId ?? undefined,
                });
            });

            markAsNoResponseRequiredMutation.mutate({
                messageId: discussion.lastTravellerUnansweredMessageId,
                reason: reason,
            });
            handleCloseMenu(event);
        };

        const messageDate = discussion.lastTravellerToAgentMessageDate || requestDate;
        const dateTime = isToday(messageDate)
            ? format(messageDate, 'HH:mm')
            : format(messageDate, 'dd/MM');

        const dateTimeContent = (
            <Typography
                variant="body2"
                component="time"
                dateTime={messageDate.toISOString()}
                color={isResponseOverdue ? 'text.primary' : 'text.secondary'}
                fontWeight={isResponseOverdue ? 700 : 400}
            >
                {dateTime}
            </Typography>
        );

        return (
            <ListItem
                sx={{
                    '&:hover': { background: paletteColors.neutral[0] },
                    '&:focus-within': { background: paletteColors.neutral[0] },
                    '&:not(:last-child)': {
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    },
                    marginBottom: 0,
                }}
                disablePadding
            >
                <Box
                    sx={{
                        width: '100%',
                        minHeight: '94px',
                        p: '12px 24px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <Link
                        to={`/dossier/${id}/discussion`}
                        onClick={() => {
                            trackEvent((ampli, defaultProperties) => {
                                ampli.newMessageClicked({
                                    ...defaultProperties,
                                    request_id: parseInt(id),
                                    message_id:
                                        discussion.lastTravellerUnansweredMessageId ?? undefined,
                                    is_overdue: isResponseOverdue,
                                });
                            });
                        }}
                        ref={ref}
                        data-testid={`message-item-${id}`}
                        style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            textDecoration: 'none',
                            color: 'inherit',
                        }}
                    >
                        <Box
                            minWidth={0}
                            sx={{ '& p': { fontWeight: discussion.hasUnreadMessages ? 700 : 400 } }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: '6px' }}>
                                <Typography
                                    variant="body1"
                                    data-testid="requester-name"
                                    sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {project.requesterName}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    color="textSecondary"
                                    sx={{ fontWeight: 700, mx: 0.5 }}
                                >
                                    •
                                </Typography>
                                <Box
                                    sx={{ display: 'flex', alignItems: 'center', mr: 1, gap: 0.5 }}
                                    component="p"
                                >
                                    {isResponseOverdue ? (
                                        <Chip
                                            component="strong"
                                            variant="filled"
                                            label={dateTimeContent}
                                            size="small"
                                            icon={
                                                <WarningIcon
                                                    fontSize="small"
                                                    aria-label={formatMessage({
                                                        id: 'homepage.response_required.warning.icon_label',
                                                    })}
                                                />
                                            }
                                            sx={{
                                                backgroundColor: paletteColors.red[100],
                                                fontWeight: 700,
                                                '& svg': {
                                                    fill: paletteColors.red[800],
                                                },
                                            }}
                                        />
                                    ) : (
                                        dateTimeContent
                                    )}
                                </Box>
                                {requesterIsRepeater && (
                                    <RepeaterBadge
                                        size={20}
                                        noMargin
                                        sx={{ marginRight: 1 }}
                                        data-testid="repeater-badge"
                                    />
                                )}
                                {step === TRAVELLING_STEP ? (
                                    <Chip
                                        component="strong"
                                        variant="filled"
                                        label={formatMessage({
                                            id: 'homepage.response_required.in_trip',
                                        })}
                                        size="small"
                                        sx={{ backgroundColor: paletteColors.blue[100] }}
                                    />
                                ) : null}
                            </Box>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                }}
                            >
                                {discussion.lastTravellerUnansweredMessageContent}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                marginLeft: 'auto',
                                gap: 1,
                            }}
                        >
                            {assignedAgent && (
                                <Avatar
                                    name={assignedAgent.name}
                                    photoUrl={assignedAgent.photoUrl}
                                />
                            )}
                            <Button
                                variant="outlined"
                                aria-controls={menuAnchorEl ? 'demo-positioned-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={menuAnchorEl ? 'true' : undefined}
                                onClick={handleOpenMenu}
                                sx={{ p: 1, minWidth: 0, width: '40px', height: '40px' }}
                                aria-labelledby={actionsButtonAccessibilityId}
                            >
                                <MoreVertRoundedIcon color="secondary" sx={{ fontSize: '24px' }} />
                                <span hidden id={actionsButtonAccessibilityId}>
                                    {formatMessage(
                                        {
                                            id: 'homepage.new_request.action.trigger_button_label',
                                        },
                                        {
                                            sender: project.requesterName,
                                        },
                                    )}
                                </span>
                            </Button>
                            <Menu
                                anchorEl={menuAnchorEl}
                                open={!!menuAnchorEl}
                                onClose={handleCloseMenu}
                                elevation={0}
                                slotProps={{
                                    paper: {
                                        sx: {
                                            mt: 0.5,
                                            borderRadius: '4px',
                                            border: '1px solid',
                                            borderColor: 'border.main',
                                        },
                                    },
                                    list: {
                                        sx: {
                                            p: 0,
                                            '& > li': {
                                                height: '56px',
                                                p: 2,
                                                '&:not(:last-child)': {
                                                    borderBottom: '1px solid',
                                                    borderColor: 'border.main',
                                                },
                                            },
                                        },
                                    },
                                }}
                            >
                                <MenuItem
                                    onClick={(event) =>
                                        handleMarkAsNoResponseRequired(
                                            event,
                                            'no_response_required',
                                        )
                                    }
                                >
                                    <Typography variant="body1">
                                        {formatMessage({
                                            id: 'homepage.new_request.action.mark_as_no_answer_required',
                                        })}
                                    </Typography>
                                </MenuItem>
                                <MenuItem
                                    onClick={(event) =>
                                        handleMarkAsNoResponseRequired(
                                            event,
                                            'external_answer_done',
                                        )
                                    }
                                >
                                    <Typography variant="body1">
                                        {formatMessage({
                                            id: 'homepage.new_request.action.mark_as_answered_outside_rm',
                                        })}
                                    </Typography>
                                </MenuItem>
                            </Menu>
                        </Box>
                    </Link>
                </Box>
            </ListItem>
        );
    },
);
